import {
  DEFAULT_LETTER_SPACING,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_TEXT_COLOR,
  TYPOGRAPHY_KEYS,
  toolbarStyleForModel,
  typographyToToolbarStyle,
} from "./tableTypography.js";

const MAX_AXIS = 40;
export const MIN_ROW_HEIGHT = 24;
export const MAX_ROW_HEIGHT = 400;

export const ACCENTS = [
  { n: "Speak2 blue", v: "#2C7BE5" },
  { n: "Navy", v: "#1F2A44" },
  { n: "Brass", v: "#B08D57" },
  { n: "Sage", v: "#5E8B6E" },
  { n: "Plum", v: "#7A4E7E" },
];

export const FILLS = [
  { v: null, n: "No fill" },
  { v: "soft", n: "Soft tint of the table color" },
  { v: "strong", n: "Strong tint of the table color" },
  { v: "#F6F1E6", n: "Cream" },
  { v: "#EDEFF3", n: "Light gray" },
  { v: "#1F2A44", n: "Navy" },
];

const emptyGrid = (rows, cols) => Array.from({ length: rows }, () => Array(cols).fill(""));

export const PRESETS = {
  schedule: {
    label: "Activity schedule",
    rows: emptyGrid(5, 3),
    widths: [22, 46, 32],
  },
  menu: {
    label: "Weekly menu",
    rows: [
      ["", "", ""],
      ["Monday", "", ""],
      ["Tuesday", "", ""],
      ["Wednesday", "", ""],
      ["Thursday", "", ""],
      ["Friday", "", ""],
      ["Saturday", "", ""],
      ["Sunday", "", ""],
    ],
    widths: [20, 38, 42],
    accent: "#B08D57",
    style: "striped",
  },
};

export const PICKER_ROWS = 6;
export const PICKER_COLS = 8;

const STYLE_MAP = {
  lined: "lines",
  boxed: "lines",
  lines: "lines",
  grid: "lines",
  plain: "minimal",
  minimal: "minimal",
  striped: "striped",
};

export function escHtml(value) {
  return String(value ?? "").replace(/[&<>"]/g, (match) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
  }[match]));
}

export function htmlToText(html) {
  return String(html ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// contentEditable emits <br>, <div><br></div>, <p><br></p> for an emptied cell; treat all as "".
export function normalizeCellHtml(html) {
  const value = String(html ?? "");
  if (/<(img|svg|video|iframe|table)\b/i.test(value)) return value;
  return htmlToText(value).replace(/\s+/g, "") === "" ? "" : value;
}

function clampCount(value, fallback) {
  return Math.max(1, Math.min(MAX_AXIS, Number(value) || fallback));
}

function cloneModel(model) {
  return JSON.parse(JSON.stringify(model));
}

function normalizeAccent(value) {
  const text = String(value || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(text)) return text;
  const short = text.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return "#2C7BE5";
}

// SECURITY: cell HTML in `rows` (and the `CellHtml` copied into TableDetails) is UNTRUSTED. It can come from a
// community-shared template. Only TableBlock's CellView makes it safe, by running sanitizeCellHtml before
// innerHTML. Anything else that renders it must call sanitizeCellHtml first (never dangerouslySetInnerHTML directly).
export function normalizeTableModel(input) {
  const source = input && typeof input === "object" ? input : {};
  const rawRows = (Array.isArray(source.rows) && source.rows.length ? source.rows : [[""]]).slice(0, MAX_AXIS);
  const cols = Math.min(MAX_AXIS, Math.max(1, ...rawRows.map((row) => (Array.isArray(row) ? row.length : 0))));
  const rows = rawRows.map((row) => {
    const next = Array.isArray(row) ? row.map((cell) => (cell == null ? "" : String(cell))) : [];
    while (next.length < cols) next.push("");
    return next.slice(0, cols);
  });
  const style = STYLE_MAP[source.style] || "lines";
  let widths = Array.isArray(source.widths) ? source.widths.map((width) => Number(width) || 0) : [];
  while (widths.length < cols) widths.push(0);
  widths = widths.slice(0, cols);
  const sum = widths.reduce((total, width) => total + width, 0);
  widths = sum > 0 ? widths.map((width) => (width * 100) / sum) : Array(cols).fill(100 / cols);

  const fit = (list, length) => {
    const next = Array.isArray(list) ? list.slice(0, length) : [];
    while (next.length < length) next.push(null);
    return next;
  };

  const cf = fit(source.cf, rows.length).map((row) => fit(row, cols));
  // A row's own minimum height in px; null follows the table default `h`.
  const rowH = fit(source.rowH, rows.length).map((value) => {
    const height = Number(value);
    return height > 0 ? Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, Math.round(height))) : null;
  });
  // SECURITY: cell patches can come from a community-shared template; readTypography keeps only known keys and
  // bounds their values.
  const cellTypo = fit(source.cellTypo, rows.length).map((row) => fit(row, cols).map(readTypography));

  return {
    rows,
    fills: fit(source.fills, rows.length),
    cfills: fit(source.cfills, cols),
    cf,
    cellTypo,
    rowH,
    widths,
    headerRow: source.headerRow == null ? source.header !== false : source.headerRow === true,
    headerCol: source.headerCol === true,
    style,
    accent: normalizeAccent(source.accent),
    size: source.size || "m",
    align: source.align || "left",
    h: Math.max(0, Math.min(140, Number(source.h) || 0)),
    frameWidth: source.frameWidth || null,
    typography: readTypography(source.typography),
  };
}

// Typography can arrive from a community-shared template, so every value is checked: text must be a short string,
// and numbers must sit in a sane range (an out-of-range number is clamped, a non-number is dropped). A value
// that is already in range is kept exactly as written ("18px" stays "18px").
const TYPOGRAPHY_NUMBER_LIMITS = {
  fontSize: [1, 500],
  fontWeight: [100, 900],
  lineHeight: [0.5, 5],
  letterSpacing: [-10, 50],
};
const MAX_TYPOGRAPHY_TEXT = 200;

function boundedTypographyValue(key, value) {
  const limits = TYPOGRAPHY_NUMBER_LIMITS[key];
  if (limits) {
    const number = parseFloat(value);
    if (!Number.isFinite(number)) return undefined;
    const [min, max] = limits;
    return number >= min && number <= max ? value : Math.max(min, Math.min(max, number));
  }
  return typeof value === "string" && value.length <= MAX_TYPOGRAPHY_TEXT ? value : undefined;
}

function readTypography(value) {
  if (!value || typeof value !== "object") return null;
  const next = {};
  TYPOGRAPHY_KEYS.forEach((key) => {
    if (value[key] == null) return;
    const bounded = boundedTypographyValue(key, value[key]);
    if (bounded !== undefined) next[key] = bounded;
  });
  return Object.keys(next).length ? next : null;
}

export function createGridModel({ rows = 3, cols = 3, header = true, style = "lines", accent } = {}) {
  const safeRows = clampCount(rows, 3);
  const safeCols = clampCount(cols, 3);
  return normalizeTableModel({
    rows: Array.from({ length: safeRows }, () => Array(safeCols).fill("")),
    headerRow: header !== false,
    headerCol: false,
    style,
    accent: accent || "#2C7BE5",
    widths: Array(safeCols).fill(100 / safeCols),
  });
}

export function createPresetModel(key) {
  const preset = PRESETS[key];
  if (!preset) return createGridModel({ rows: 3, cols: 3, header: true });
  return normalizeTableModel({
    rows: preset.rows.map((row) => row.map((cell) => escHtml(cell))),
    widths: preset.widths,
    accent: preset.accent,
    style: preset.style || "lines",
    headerRow: true,
    headerCol: false,
    frameWidth: 640,
  });
}

export function resolveTableModel(element) {
  if (element?.TableModel?.rows?.length) return normalizeTableModel(element.TableModel);
  const details = element?.TableDetails || element || [];
  if (!Array.isArray(details) || !details.length) return createGridModel({ rows: 3, cols: 3, header: true });

  const rows = details.map((row) =>
    (row?.RowCells || []).map((cell) => {
      if (cell?.CellHtml) return String(cell.CellHtml);
      return escHtml(cell?.CellData || "");
    }),
  );
  const first = details[0]?.RowCells || [];
  const widths = first.map((cell) => Number(cell?.CellStyles?.CellWidth) || 1);
  const bordered = first.some((cell) => Number(cell?.CellStyles?.CellBorderWidth) > 0);
  // Cells without a stored weight say nothing either way, so only judge the ones that have one.
  const weights = first
    .map((cell) => Number(cell?.CellStyles?.CellFontWeight))
    .filter((weight) => Number.isFinite(weight) && weight > 0);
  const headerRow = weights.length > 0 && weights.every((weight) => weight >= 600);

  return normalizeTableModel({
    rows,
    widths,
    headerRow,
    headerCol: false,
    style: bordered ? "lines" : "minimal",
    accent: "#2C7BE5",
  });
}

export function mixWithWhite(hex, amount) {
  if (!/^#[0-9a-f]{6}$/i.test(hex || "")) return "#ffffff";
  const num = parseInt(hex.slice(1), 16);
  const channel = (shift) => Math.round(((num >> shift) & 255) * amount + 255 * (1 - amount));
  return `#${[16, 8, 0].map((shift) => channel(shift).toString(16).padStart(2, "0")).join("")}`;
}

// Hex only (no color-mix): html2canvas, DOCX export and the legacy toolbar can't parse color-mix.
function fillHex(value, accent) {
  if (!value) return null;
  if (value === "soft") return mixWithWhite(accent, 0.12);
  if (value === "strong") return mixWithWhite(accent, 0.28);
  return value;
}

const INK = DEFAULT_TEXT_COLOR;
const LINE = "#C9CED8";
const FONT = "DM Sans";

// Mirrors what TableBlock.css draws, so the DOCX export (which reads TableDetails) matches the screen.
function cellStyle(model, rowIndex, colIndex, previous) {
  const plain = model.style === "minimal" || model.style === "striped";
  const solidHeader = model.style === "lines" || model.style === "striped";
  const isHeader = model.headerRow && rowIndex === 0;
  const isHeaderCol = model.headerCol && colIndex === 0 && !isHeader;
  const prev = previous?.CellStyles || {};
  const typo = cellTypography(model, rowIndex, colIndex);
  const explicit = cellFill(model, rowIndex, colIndex);
  const fill = fillHex(explicit, model.accent);
  const [, fillText] = fill ? fillCSSPair(model, explicit) : [null, null];
  const fontSize = Number(String(typo.fontSize ?? "").replace(/px$/i, ""));

  let background = "#ffffff";
  let text = INK;
  if (isHeader && solidHeader) {
    background = model.accent;
    text = "#FFFFFF";
  } else if (isHeader) {
    text = model.accent;
  } else if (isHeaderCol && model.style === "lines") {
    background = mixWithWhite(model.accent, 0.11);
  } else if (isHeaderCol) {
    text = model.accent;
  } else if (model.style === "striped" && rowIndex % 2 === 1) {
    background = mixWithWhite(model.accent, 0.08);
  }

  const frameWidth = model.frameWidth || Math.min(760, Math.max(220, model.rows[0].length * 150));
  const cellWidth = Math.max(20, Math.round((frameWidth * (model.widths[colIndex] || 0)) / 100));

  return {
    CellWidth: cellWidth,
    CellZIndex: prev.CellZIndex ?? 10,
    CellMinHeight: prev.CellMinHeight || 30,
    CellMinWidth: prev.CellMinWidth || 100,
    CellBorderPatteren: plain ? "none" : "solid",
    CellBorderColor: plain ? prev.CellBorderColor || LINE : isHeader ? model.accent : LINE,
    CellBorderWidth: plain ? 0 : 1,
    CellBackgroundColor: fill || typo.background || background,
    CellBackgroundImage: prev.CellBackgroundImage ?? null,
    CellBorderImage: prev.CellBorderImage ?? null,
    CellBackgroundImageRepeatStatus: prev.CellBackgroundImageRepeatStatus === true,
    CellBorderIsImage: prev.CellBorderIsImage === true,
    CellBorderRadius: prev.CellBorderRadius || 0,
    CellBorderImageName: prev.CellBorderImageName ?? null,
    CellBackgroundImageName: prev.CellBackgroundImageName ?? null,
    CellOpacity: prev.CellOpacity ?? 1,
    CellFontFamily: typo.fontFamily || FONT,
    CellFontSize: fontSize > 0 ? fontSize : prev.CellFontSize || 15,
    CellFontStyle: typo.fontStyle || prev.CellFontStyle || "Normal",
    CellFontWeight: typo.fontWeight || prev.CellFontWeight || (isHeader ? 700 : 400),
    CellLetterSpacing: typo.letterSpacing ?? prev.CellLetterSpacing ?? DEFAULT_LETTER_SPACING,
    CellLineHeight: typo.lineHeight || prev.CellLineHeight || DEFAULT_LINE_HEIGHT,
    CellTextColor: typo.color || (fill ? fillText : text),
    CellTextAlignment: typo.align || model.align || prev.CellTextAlignment || "left",
    CellTextDecoration: typo.decoration ?? prev.CellTextDecoration ?? "",
  };
}

export function modelToTableDetails(model, previous = []) {
  const next = normalizeTableModel(model);
  return next.rows.map((row, rowIndex) => ({
    RowHeight: next.rowH[rowIndex] || next.h || previous?.[rowIndex]?.RowHeight || 50,
    IsSelectedRow: false,
    RowCells: row.map((html, colIndex) => {
      const prev = previous?.[rowIndex]?.RowCells?.[colIndex];
      return {
        CellData: htmlToText(html),
        // SECURITY: raw, unsanitized cell HTML (see normalizeTableModel). Sanitize before rendering as HTML.
        CellHtml: html,
        IsCellActive: false,
        CellStyles: cellStyle(next, rowIndex, colIndex, prev),
      };
    }),
  }));
}

export function frameForModel(model) {
  const next = normalizeTableModel(model);
  const cols = next.rows[0].length;
  const width = next.frameWidth || Math.min(760, Math.max(220, cols * 150));
  const rowUnit = next.h > 0 ? next.h : 50;
  const height = next.rowH.reduce((total, own) => total + (own || rowUnit), 0);
  return {
    width,
    height: Math.max(56, height),
  };
}

// Table typography, then the cell's own overrides on top.
export function cellTypography(model, rowIndex, colIndex) {
  return { ...(model?.typography || {}), ...(model?.cellTypo?.[rowIndex]?.[colIndex] || {}) };
}

const sizeOf = (value) => Number(String(value ?? "").replace(/px$/i, "")) || 0;

// Font family and size are the two toolbar fields that can show "mixed". True when the cells in `rect`
// (the whole table when null) do not all draw the same value.
export function mixedToolbarFields(model, rect) {
  // The table passes a normalized model on every keystroke, so it is only normalized here when it is not already.
  const next = Array.isArray(model?.cellTypo) && Array.isArray(model?.rows) ? model : normalizeTableModel(model);
  const target = clampRect(next, rect) || {
    r0: 0,
    r1: next.rows.length - 1,
    c0: 0,
    c1: next.rows[0].length - 1,
  };
  // What a cell without its own value draws, computed once. A cell's own value wins when it has one.
  const base = toolbarStyleForModel(next);
  const baseSize = sizeOf(base.ElementFontSize);
  const has = (value) => value != null && value !== "";
  const families = new Set();
  const sizes = new Set();
  for (let row = target.r0; row <= target.r1; row += 1) {
    for (let col = target.c0; col <= target.c1; col += 1) {
      const own = next.cellTypo[row]?.[col];
      families.add(has(own?.fontFamily) ? own.fontFamily : base.ElementFontFamily);
      sizes.add(has(own?.fontSize) ? sizeOf(own.fontSize) : baseSize);
    }
  }
  return { fontFamily: families.size > 1, fontSize: sizes.size > 1 };
}

// The values the toolbar's saved style carries: the anchor cell's, or the table's own when no cell is targeted.
// Always real values. Whether the target is mixed is kept out of the design (see mixedToolbarFields and tableSelection).
export function toolbarStyleForTarget(model, cell = null) {
  const typography = cell ? cellTypography(model, cell.row, cell.col) : model?.typography;
  const style = typographyToToolbarStyle(model, typography);
  // A color or weight nobody set is still drawn: header text is white on the accent, a filled cell has its own text
  // color, a header is bold. The toolbar shows what the cell really draws, not its own black. With no cell targeted
  // (the whole table), that is a body cell: it is what most of the table looks like.
  const next = Array.isArray(model?.cellTypo) && Array.isArray(model?.rows) ? model : normalizeTableModel(model);
  const rows = next.rows.length;
  const cols = next.rows[0].length;
  const at = cell || { row: next.headerRow && rows > 1 ? 1 : 0, col: next.headerCol && cols > 1 ? 1 : 0 };
  const drawn = cellStyle(next, Math.max(0, Math.min(rows - 1, at.row)), Math.max(0, Math.min(cols - 1, at.col)));
  const fromDrawn = {
    ElementTextColor: drawn.CellTextColor,
    ElementBackgroundColor: drawn.CellBackgroundColor,
    ElementFontWeight: drawn.CellFontWeight,
  };
  Object.keys(fromDrawn).forEach((field) => {
    // An explicit fill is drawn over a toolbar background, so the background always reports what is drawn.
    const fillWins = field === "ElementBackgroundColor";
    if ((fillWins || style[field] === undefined) && fromDrawn[field] != null && fromDrawn[field] !== "") style[field] = fromDrawn[field];
  });
  return style;
}

// Everything the toolbar reads from a saved model except the cell text (sizes, fonts, colors, the grid's shape).
// Typing changes only the text, so a table can skip its toolbar work while the signature stays the same.
// Null when there is no model to read (a legacy table that only has TableDetails).
export function tableStyleSignature(model) {
  if (!Array.isArray(model?.rows) || !model.rows.length) return null;
  const { rows, ...rest } = model;
  const cols = Math.max(0, ...rows.map((row) => (Array.isArray(row) ? row.length : 0)));
  return `${rows.length}x${cols}|${JSON.stringify(rest)}`;
}

// A toolbar change goes to the targeted cells only. With no target it is table-wide: the table's typography
// takes it, and each cell drops its own override of just those fields, so the cells read the same on them.
export function applyToolbarPatch(model, patch, rect) {
  const next = cloneModel(normalizeTableModel(model));
  const picked = readTypography(patch);
  if (!picked) return next;
  const target = clampRect(next, rect);
  // No rect means the whole table. A rect that no longer fits the table (it shrank under the selection) changes
  // nothing, so a stale target can never turn a one-cell edit into a whole-table edit.
  if (!target && rect) return next;
  if (!target) {
    next.typography = { ...(next.typography || {}), ...picked };
    // The last background picked wins over every earlier one, so fills painted before it are dropped.
    if (picked.background) {
      next.cf = next.cf.map((row) => row.map(() => null));
      next.fills = next.fills.map(() => null);
      next.cfills = next.cfills.map(() => null);
    }
    next.cellTypo = next.cellTypo.map((row) =>
      row.map((own) => {
        if (!own) return null;
        const kept = { ...own };
        Object.keys(picked).forEach((key) => delete kept[key]);
        return Object.keys(kept).length ? kept : null;
      }),
    );
    return normalizeMutated(next);
  }
  for (let row = target.r0; row <= target.r1; row += 1) {
    for (let col = target.c0; col <= target.c1; col += 1) {
      next.cellTypo[row][col] = { ...(next.cellTypo[row][col] || {}), ...picked };
      // A picked background replaces any earlier fill on the cell (fills are drawn over it otherwise).
      if (picked.background) next.cf[row][col] = picked.background;
    }
  }
  return normalizeMutated(next);
}

const MIN_FONT = 1;
const MAX_FONT = 500;
const clampFont = (size) => String(Math.max(MIN_FONT, Math.min(MAX_FONT, size)));

// +/- on a table whose cells differ: every cell moves by `delta` from its own size. A range steps its cells;
// the whole table steps its base size and each cell's own size, so cells without an override stay inherited.
export function stepFontSize(model, delta, rect) {
  const next = cloneModel(normalizeTableModel(model));
  const target = clampRect(next, rect);
  if (!target && rect) return next; // a stale target changes nothing (see applyToolbarPatch)
  const sizeFor = (row, col) => sizeOf(typographyToToolbarStyle(next, cellTypography(next, row, col)).ElementFontSize);
  if (target) {
    for (let row = target.r0; row <= target.r1; row += 1) {
      for (let col = target.c0; col <= target.c1; col += 1) {
        next.cellTypo[row][col] = { ...(next.cellTypo[row][col] || {}), fontSize: clampFont(sizeFor(row, col) + delta) };
      }
    }
    return normalizeMutated(next);
  }
  const base = sizeOf(typographyToToolbarStyle(next, next.typography).ElementFontSize);
  next.typography = { ...(next.typography || {}), fontSize: clampFont(base + delta) };
  next.cellTypo = next.cellTypo.map((row) =>
    row.map((own) => (own && own.fontSize != null ? { ...own, fontSize: clampFont(sizeOf(own.fontSize) + delta) } : own)),
  );
  return normalizeMutated(next);
}

// One row's own minimum height; null (or 0) puts the row back on the table default.
export function setRowHeight(model, rowIndex, height) {
  const next = cloneModel(normalizeTableModel(model));
  if (rowIndex < 0 || rowIndex >= next.rows.length) return next;
  next.rowH[rowIndex] = height > 0 ? height : null;
  return normalizeMutated(next);
}

// Every row's own minimum height at once, in row order (null puts a row back on the default). The table corner
// drag uses it to scale all rows together without losing how they differ.
export function setRowHeights(model, heights) {
  const next = cloneModel(normalizeTableModel(model));
  next.rowH = next.rowH.map((_, row) => (heights?.[row] > 0 ? heights[row] : null));
  return normalizeMutated(next);
}

// The table-wide row height (flyout slider): every row follows it, so per-row heights are dropped.
export function withDefaultRowHeight(model, height) {
  const next = cloneModel(normalizeTableModel(model));
  next.h = Math.max(0, Math.min(140, Number(height) || 0));
  next.rowH = next.rowH.map(() => null);
  return normalizeMutated(next);
}

export function hasCellTypo(model) {
  return (model?.cellTypo || []).some((row) => row.some(Boolean));
}

export function mergeTableEditorUpdate(element, reference) {
  const frame = reference?.frame || {};
  return {
    ...element,
    // Only an update that carries a model writes one. A style-only update (toolbar sync) must leave the element
    // alone: a legacy table has no TableModel yet, and normalizing nothing would replace its content with a blank one.
    ...(reference?.model ? { TableModel: normalizeTableModel(reference.model) } : null),
    TableDetails: reference?.tableDetails || element?.TableDetails,
    Styles: (element?.Styles || []).map((style, index) => {
      if (index !== 0) return style;
      return {
        ...style,
        ...(reference?.toolbarStyle || {}),
        ElementWidth: frame.width ?? style.ElementWidth,
        ElementHeight: frame.height ?? style.ElementHeight,
        ElementLeft: frame.left ?? style.ElementLeft,
        ElementTop: frame.top ?? style.ElementTop,
      };
    }),
  };
}

function normalizeMutated(model) {
  return normalizeTableModel(model);
}

export function insRow(model, at, copyFrom) {
  const next = cloneModel(normalizeTableModel(model));
  const cols = next.rows[0].length;
  const index = Math.max(0, Math.min(next.rows.length, at));
  const source = copyFrom ? next.rows[copyFrom - 1] : null;
  next.rows.splice(index, 0, source ? source.slice() : Array(cols).fill(""));
  next.fills.splice(index, 0, copyFrom ? next.fills[copyFrom - 1] || null : null);
  next.cf.splice(index, 0, copyFrom ? (next.cf[copyFrom - 1] || Array(cols).fill(null)).slice() : Array(cols).fill(null));
  next.cellTypo.splice(index, 0, copyFrom ? (next.cellTypo[copyFrom - 1] || Array(cols).fill(null)).slice() : Array(cols).fill(null));
  next.rowH.splice(index, 0, copyFrom ? next.rowH[copyFrom - 1] ?? null : null);
  return normalizeMutated(next);
}

export function insCol(model, at) {
  const next = cloneModel(normalizeTableModel(model));
  const index = Math.max(0, Math.min(next.rows[0].length, at));
  const added = 100 / (next.rows[0].length + 1);
  next.widths = next.widths.map((width) => (width * (100 - added)) / 100);
  next.widths.splice(index, 0, added);
  next.cfills.splice(index, 0, null);
  next.rows.forEach((row) => row.splice(index, 0, ""));
  next.cf.forEach((row) => row.splice(index, 0, null));
  next.cellTypo.forEach((row) => row.splice(index, 0, null));
  return normalizeMutated(next);
}

export function delRow(model, rowIndex) {
  const next = cloneModel(normalizeTableModel(model));
  if (next.rows.length <= 1) return { ok: false, message: "A table needs at least one row." };
  next.rows.splice(rowIndex, 1);
  next.fills.splice(rowIndex, 1);
  next.cf.splice(rowIndex, 1);
  next.cellTypo.splice(rowIndex, 1);
  next.rowH.splice(rowIndex, 1);
  return { ok: true, model: normalizeMutated(next) };
}

export function delCol(model, colIndex) {
  const next = cloneModel(normalizeTableModel(model));
  if (next.rows[0].length <= 1) return { ok: false, message: "A table needs at least one column." };
  next.cfills.splice(colIndex, 1);
  next.rows.forEach((row) => row.splice(colIndex, 1));
  next.widths.splice(colIndex, 1);
  next.cf.forEach((row) => row.splice(colIndex, 1));
  next.cellTypo.forEach((row) => row.splice(colIndex, 1));
  return { ok: true, model: normalizeMutated(next) };
}

export function moveRow(model, rowIndex, direction) {
  const next = cloneModel(normalizeTableModel(model));
  const target = rowIndex + direction;
  if (target < 0 || target >= next.rows.length) return next;
  [next.rows[rowIndex], next.rows[target]] = [next.rows[target], next.rows[rowIndex]];
  [next.fills[rowIndex], next.fills[target]] = [next.fills[target], next.fills[rowIndex]];
  [next.cf[rowIndex], next.cf[target]] = [next.cf[target], next.cf[rowIndex]];
  [next.cellTypo[rowIndex], next.cellTypo[target]] = [next.cellTypo[target], next.cellTypo[rowIndex]];
  [next.rowH[rowIndex], next.rowH[target]] = [next.rowH[target], next.rowH[rowIndex]];
  return normalizeMutated(next);
}

export function moveCol(model, colIndex, direction) {
  const next = cloneModel(normalizeTableModel(model));
  const target = colIndex + direction;
  if (target < 0 || target >= next.rows[0].length) return next;
  next.rows.forEach((row) => {
    [row[colIndex], row[target]] = [row[target], row[colIndex]];
  });
  next.cf.forEach((row) => {
    [row[colIndex], row[target]] = [row[target], row[colIndex]];
  });
  next.cellTypo.forEach((row) => {
    [row[colIndex], row[target]] = [row[target], row[colIndex]];
  });
  [next.widths[colIndex], next.widths[target]] = [next.widths[target], next.widths[colIndex]];
  [next.cfills[colIndex], next.cfills[target]] = [next.cfills[target], next.cfills[colIndex]];
  return normalizeMutated(next);
}

export function sortCol(model, colIndex, direction) {
  const next = cloneModel(normalizeTableModel(model));
  const headCount = next.headerRow ? 1 : 0;
  const head = next.rows.slice(0, headCount).map((row, index) => [row, next.fills[index], next.cf[index], next.cellTypo[index], next.rowH[index]]);
  const body = next.rows.slice(headCount).map((row, index) => [
    row,
    next.fills[index + headCount],
    next.cf[index + headCount],
    next.cellTypo[index + headCount],
    next.rowH[index + headCount],
  ]);
  body.sort((a, b) => {
    const left = htmlToText(a[0][colIndex]).trim();
    const right = htmlToText(b[0][colIndex]).trim();
    if (!left && right) return 1;
    if (left && !right) return -1;
    return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" }) * direction;
  });
  const all = head.concat(body);
  next.rows = all.map((item) => item[0]);
  next.fills = all.map((item) => item[1]);
  next.cf = all.map((item) => item[2]);
  next.cellTypo = all.map((item) => item[3]);
  next.rowH = all.map((item) => item[4]);
  return normalizeMutated(next);
}

export function setCount(model, kind, count) {
  let next = normalizeTableModel(model);
  const target = clampCount(count, kind === "rows" ? next.rows.length : next.rows[0].length);
  if (kind === "rows") {
    while (next.rows.length < target) next = insRow(next, next.rows.length);
    while (next.rows.length > target) {
      const removed = delRow(next, next.rows.length - 1);
      if (!removed.ok) break;
      next = removed.model;
    }
  } else {
    while (next.rows[0].length < target) next = insCol(next, next.rows[0].length);
    while (next.rows[0].length > target) {
      const removed = delCol(next, next.rows[0].length - 1);
      if (!removed.ok) break;
      next = removed.model;
    }
  }
  return next;
}

function dropBackground(own) {
  if (!own || own.background === undefined) return own;
  const kept = { ...own };
  delete kept.background;
  return Object.keys(kept).length ? kept : null;
}

export function setRowFill(model, rowIndex, value) {
  const next = cloneModel(normalizeTableModel(model));
  next.fills[rowIndex] = value || null;
  // A fill chosen now replaces the colors cells in this row were given earlier (cell fills and toolbar backgrounds).
  if (value) next.cf[rowIndex] = next.cf[rowIndex].map(() => null);
  if (value) next.cellTypo[rowIndex] = next.cellTypo[rowIndex].map((own) => dropBackground(own));
  return normalizeMutated(next);
}

export function setColFill(model, colIndex, value) {
  const next = cloneModel(normalizeTableModel(model));
  next.cfills[colIndex] = value || null;
  if (value) {
    next.cf.forEach((row) => { row[colIndex] = null; });
    next.cellTypo.forEach((row) => { row[colIndex] = dropBackground(row[colIndex]); });
  }
  return normalizeMutated(next);
}

export function setCellFill(model, rowIndex, colIndex, value) {
  const next = cloneModel(normalizeTableModel(model));
  next.cf[rowIndex][colIndex] = value || null;
  return normalizeMutated(next);
}

export function clampRect(model, rect) {
  if (!rect) return null;
  const rows = model?.rows?.length || 0;
  const cols = model?.rows?.[0]?.length || 0;
  if (!rows || !cols || rect.r0 > rows - 1 || rect.c0 > cols - 1) return null;
  return {
    r0: Math.max(0, rect.r0),
    r1: Math.min(rows - 1, rect.r1),
    c0: Math.max(0, rect.c0),
    c1: Math.min(cols - 1, rect.c1),
  };
}

export function setRangeFill(model, rect, value) {
  let next = normalizeTableModel(model);
  rect = clampRect(next, rect);
  if (!rect) return next;
  for (let row = rect.r0; row <= rect.r1; row += 1) {
    for (let col = rect.c0; col <= rect.c1; col += 1) {
      next = setCellFill(next, row, col, value);
    }
  }
  return next;
}

export function clearRangeText(model, rect) {
  const next = cloneModel(normalizeTableModel(model));
  rect = clampRect(next, rect);
  if (!rect) return next;
  for (let row = rect.r0; row <= rect.r1; row += 1) {
    for (let col = rect.c0; col <= rect.c1; col += 1) {
      next.rows[row][col] = "";
    }
  }
  return normalizeMutated(next);
}

export function clearStructuralFills(model) {
  const next = cloneModel(normalizeTableModel(model));
  next.fills = next.fills.map(() => null);
  next.cfills = next.cfills.map(() => null);
  return normalizeMutated(next);
}

export function setCellHtml(model, rowIndex, colIndex, html) {
  const next = cloneModel(normalizeTableModel(model));
  next.rows[rowIndex][colIndex] = normalizeCellHtml(html);
  return next;
}

export function applyPaste(model, rowIndex, colIndex, grid) {
  let next = normalizeTableModel(model);
  const width = Math.max(...grid.map((row) => row.length), 1);
  const maxRows = Math.min(MAX_AXIS, rowIndex + grid.length);
  const maxCols = Math.min(MAX_AXIS, colIndex + width);
  while (next.rows.length < maxRows) next = insRow(next, next.rows.length);
  while (next.rows[0].length < maxCols) next = insCol(next, next.rows[0].length);
  const cloned = cloneModel(next);
  grid.forEach((row, rowOffset) => {
    if (rowIndex + rowOffset >= cloned.rows.length) return;
    row.forEach((value, colOffset) => {
      if (colIndex + colOffset >= cloned.rows[0].length) return;
      cloned.rows[rowIndex + rowOffset][colIndex + colOffset] = escHtml(String(value).trim());
    });
  });
  return normalizeMutated(cloned);
}

export function cellFill(model, rowIndex, colIndex) {
  return model.cf?.[rowIndex]?.[colIndex] || model.fills?.[rowIndex] || model.cfills?.[colIndex] || null;
}

export function fillCSS(value, accent) {
  if (!value) return "";
  if (value === "soft") return mixWithWhite(accent, 0.12);
  if (value === "strong") return mixWithWhite(accent, 0.28);
  return value;
}

function isDark(hex) {
  if (!/^#[0-9a-f]{6}$/i.test(hex || "")) return false;
  const num = parseInt(hex.slice(1), 16);
  const red = (num >> 16) & 255;
  const green = (num >> 8) & 255;
  const blue = num & 255;
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue < 140;
}

export function fillCSSPair(model, value) {
  const background = fillCSS(value, model.accent);
  const foreground = value === "soft" || value === "strong" ? INK : isDark(value) ? "#FFFFFF" : INK;
  return [background, foreground];
}

export function selectionRect(range) {
  if (!range) return null;
  return {
    r0: Math.min(range.r1, range.r2),
    r1: Math.max(range.r1, range.r2),
    c0: Math.min(range.c1, range.c2),
    c1: Math.max(range.c1, range.c2),
  };
}

export function selectionCount(range) {
  const rect = selectionRect(range);
  if (!rect) return 0;
  return (rect.r1 - rect.r0 + 1) * (rect.c1 - rect.c0 + 1);
}

export function hasStructuralFill(model) {
  return (model?.fills || []).some(Boolean) || (model?.cfills || []).some(Boolean);
}
